import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ImportApplicationSource, {
  ImportWorkingView,
  repoLabelFromUrl,
} from "../../components/agents/ImportApplicationSource";
import { showNotificationGlobal } from "../../components/common/NotificationProvider";
import CodeIntelligenceService from "../../services/api/codeIntelligence";
import AgentService from "../../services/api/agents";
import { API_RESPONSE, default_size } from "../../settings/shared/constants";
import CodeIntelligenceOverview from "./CodeIntelligenceOverview";
import PremiumFeatureLocked from "../../components/common/PremiumFeatureLocked";
import { isPocTenant } from "../../utils/premiumAccess";

const PAGE_CSS = `
  .sa-import-page {
    flex: 1;
    min-height: 0;
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background:
      radial-gradient(ellipse 80% 46% at 8% -8%, color-mix(in srgb, var(--activemenu, #3d91e4) 16%, transparent), transparent 58%),
      radial-gradient(ellipse 55% 38% at 100% 0%, rgba(99, 102, 241, 0.08), transparent 52%),
      #f5f7fb;
    font-family: 'DM Sans', system-ui, sans-serif;
  }
  .sa-import-page-header {
    flex-shrink: 0;
    background: rgba(255,255,255,0.82);
    backdrop-filter: blur(16px);
    border-bottom: 1px solid rgba(15, 23, 42, 0.06);
    padding: 16px 24px 14px;
  }
  .sa-import-page.is-working .sa-import-page-header,
  .sa-import-page.is-working .sa-import-page-back {
    display: none;
  }
  .sa-import-page-header-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px 24px;
    flex-wrap: wrap;
  }
  .sa-import-page.is-overview .sa-import-page-header-row {
    align-items: center;
  }
  .sa-import-page-heading { min-width: 0; flex: 1 1 220px; }
  .sa-import-kpi-slot {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex: 1 1 360px;
    min-width: 0;
  }
  .ci-kpi-strip {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: flex-end;
    width: 100%;
  }
  .ci-kpi-chip {
    border: 1px solid color-mix(in srgb, var(--activemenu, #3d91e4) 18%, #e2e8f0);
    background: #fff;
    color: inherit;
    border-radius: 10px;
    padding: 6px 10px;
    text-align: left;
    cursor: pointer;
    font-family: inherit;
    min-width: 92px;
  }
  .ci-kpi-chip:hover {
    border-color: var(--activemenu, #3d91e4);
    background: color-mix(in srgb, var(--activemenu, #3d91e4) 8%, #ffffff);
  }
  .ci-kpi-chip .label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #94a3b8;
  }
  .ci-kpi-chip .value {
    font-size: 13.5px;
    font-weight: 750;
    letter-spacing: -0.02em;
    color: #0f172a;
    margin-top: 1px;
    white-space: nowrap;
    max-width: 140px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .sa-import-page-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    flex-shrink: 0;
    margin-left: auto;
    flex-wrap: wrap;
  }
  .sa-import-page-back,
  .sa-xray-import-btn {
    border-radius: 10px;
    padding: 8px 13px;
    font-size: 13px;
    font-weight: 650;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    flex-shrink: 0;
    font-family: inherit;
    transition: transform 0.14s ease, box-shadow 0.14s ease, filter 0.14s ease, border-color 0.14s ease, background 0.14s ease, color 0.14s ease;
  }
  .sa-import-page-back {
    border: 1px solid #e2e8f0;
    background: #fff;
    color: #334155;
  }
  .sa-import-page-back:hover {
    border-color: color-mix(in srgb, var(--activemenu, #3d91e4) 35%, #e2e8f0);
    color: var(--activemenu, #3d91e4);
  }
  .sa-xray-import-btn {
    border: none;
    background: var(--activemenu, #3d91e4);
    color: #fff;
    box-shadow: 0 6px 16px color-mix(in srgb, var(--activemenu, #3d91e4) 28%, transparent);
  }
  .sa-xray-import-btn:hover {
    filter: brightness(1.05);
    transform: translateY(-1px);
  }
  .sa-import-page-kicker {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 22px;
    padding: 0 9px 0 7px;
    border-radius: 999px;
    font-size: 10.5px;
    font-weight: 750;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--activemenu, #3d91e4);
    background: color-mix(in srgb, var(--activemenu, #3d91e4) 12%, #ffffff);
    border: 1px solid color-mix(in srgb, var(--activemenu, #3d91e4) 22%, transparent);
    margin-bottom: 10px;
  }
  .sa-import-page-heading h1 {
    margin: 0 0 4px;
    font-size: 1.42rem;
    font-weight: 750;
    color: #0f172a;
    letter-spacing: -0.03em;
    line-height: 1.2;
  }
  .sa-import-page-heading p {
    margin: 0;
    font-size: 13.5px;
    color: #64748b;
    line-height: 1.45;
    max-width: 540px;
  }
  .sa-import-page-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 22px 24px 32px;
    background: transparent;
  }
  .sa-import-page.is-working .sa-import-page-body {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    overflow: hidden;
  }
  .sa-import-page-body-inner {
    width: 100%;
    max-width: none;
    margin: 0;
  }
  .sa-import-page-body-inner.is-working {
    max-width: none;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 64px 24px 40px;
  }
  .sa-import-page-body-inner.is-overview { max-width: none; }
  .sa-import-page.is-files .sa-import-page-header {
    padding-top: 10px;
    padding-bottom: 8px;
  }
  .sa-import-page.is-files .sa-import-page-heading p {
    display: none;
  }
  .sa-import-page.is-files .sa-import-page-body {
    overflow: hidden;
    display: flex;
    flex-direction: column;
    padding-top: 12px;
    padding-bottom: 12px;
  }
  .sa-import-page.is-files .sa-import-page-body-inner.is-overview {
    flex: 1;
    min-height: 0;
    max-width: none;
    display: flex;
    flex-direction: column;
  }
  .sa-import-page-card {
    background: transparent;
    border: none;
    border-radius: 0;
    padding: 8px 0 0;
    box-shadow: none;
  }
  .sa-import-page-card.is-form {
    background: #fff;
    border: 1px solid rgba(15, 23, 42, 0.06);
    border-radius: 18px;
    padding: 20px 20px 16px;
    box-shadow: 0 14px 36px rgba(15, 23, 42, 0.06);
  }
  .sa-import-page.is-working .sa-import-page-card {
    background: transparent;
    border: none;
    padding: 0;
    width: 100%;
    max-width: 560px;
  }
  @media (max-width: 900px) {
    .sa-import-page-header,
    .sa-import-page-body {
      padding-left: 12px;
      padding-right: 12px;
    }
    .sa-import-page-card { padding: 8px 0 0; }
  }
  .sa-xray-home {
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-height: calc(100vh - 188px);
  }
  .sa-xray-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }
  .sa-xray-count {
    font-size: 13px;
    font-weight: 650;
    color: #475569;
  }
  .sa-xray-count strong {
    color: #0f172a;
    font-variant-numeric: tabular-nums;
  }
  .sa-xray-grid {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 16px;
  }
  .sa-xray-card {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
    text-align: left;
    border: 1px solid rgba(15, 23, 42, 0.08);
    background: #fff;
    border-radius: 16px;
    padding: 16px;
    overflow: hidden;
    cursor: pointer;
    font-family: inherit;
    color: inherit;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
    transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
  }
  .sa-xray-card:hover,
  .sa-xray-card:focus-visible {
    transform: translateY(-3px);
    border-color: color-mix(in srgb, var(--activemenu, #3d91e4) 32%, #e2e8f0);
    box-shadow: 0 16px 36px rgba(15, 23, 42, 0.08);
    outline: none;
  }
  .sa-xray-card-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }
  .sa-xray-card-icon {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: color-mix(in srgb, var(--activemenu, #3d91e4) 12%, #ffffff);
    color: var(--activemenu, #3d91e4);
    font-size: 16px;
    flex-shrink: 0;
  }
  .sa-xray-card-title {
    font-size: 15.5px;
    font-weight: 750;
    color: #0f172a;
    letter-spacing: -0.02em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.3;
  }
  .sa-xray-card-source {
    font-size: 12.5px;
    color: #64748b;
    font-weight: 600;
  }
  .sa-xray-card-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding-top: 10px;
    border-top: 1px solid #f1f5f9;
  }
  .sa-xray-card-meta span {
    font-size: 12px;
    color: #94a3b8;
    font-weight: 500;
  }
  .sa-xray-card-meta .fa-arrow-right {
    color: #94a3b8;
    font-size: 12px;
  }
  .sa-xray-card:hover .sa-xray-card-meta .fa-arrow-right {
    color: var(--activemenu, #3d91e4);
  }
  .sa-xray-scan-status {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
    font-size: 10.5px;
    font-weight: 750;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    border-radius: 999px;
    padding: 4px 9px;
  }
  .sa-xray-scan-status.is-ready {
    background: #ecfdf5;
    color: #047857;
  }
  .sa-xray-scan-status.is-busy {
    background: #eff6ff;
    color: #1d4ed8;
  }
  .sa-xray-scan-status.is-failed {
    background: #fef2f2;
    color: #b91c1c;
  }
  .sa-xray-empty,
  .sa-xray-error {
    margin: auto;
    width: min(720px, 100%);
    border: 1px solid rgba(15, 23, 42, 0.07);
    border-radius: 22px;
    padding: 36px 28px 32px;
    text-align: center;
    color: #64748b;
    background: #fff;
    box-shadow: 0 18px 50px rgba(15, 23, 42, 0.06);
    position: relative;
    overflow: hidden;
  }
  .sa-xray-empty-glow {
    position: absolute;
    width: 280px;
    height: 280px;
    border-radius: 50%;
    top: -120px;
    left: 50%;
    transform: translateX(-50%);
    background: radial-gradient(circle, color-mix(in srgb, var(--activemenu, #3d91e4) 22%, transparent), transparent 68%);
    pointer-events: none;
  }
  .sa-xray-empty-icon {
    width: 64px;
    height: 64px;
    margin: 0 auto 16px;
    border-radius: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: color-mix(in srgb, var(--activemenu, #3d91e4) 12%, #ffffff);
    color: var(--activemenu, #3d91e4);
    font-size: 26px;
    position: relative;
    box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--activemenu, #3d91e4) 16%, transparent);
  }
  .sa-xray-empty h3,
  .sa-xray-error h3 {
    margin: 0 0 8px;
    font-size: 1.32rem;
    font-weight: 750;
    letter-spacing: -0.03em;
    color: #0f172a;
    position: relative;
  }
  .sa-xray-empty p,
  .sa-xray-error p {
    margin: 0 auto 22px;
    max-width: 440px;
    font-size: 14px;
    line-height: 1.55;
    position: relative;
  }
  .sa-xray-empty .sa-xray-import-btn {
    margin: 0 auto 4px;
  }
  .sa-xray-trust {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 14px 18px;
    margin-top: 18px;
    position: relative;
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
  }
  .sa-xray-trust span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .sa-xray-trust i { color: var(--activemenu, #3d91e4); font-size: 11px; }
  .sa-xray-error i {
    font-size: 22px;
    color: #dc2626;
    margin-bottom: 10px;
    display: inline-block;
  }
  .sa-xray-skeleton {
    height: 164px;
    border-radius: 16px;
    background: linear-gradient(90deg, #eef2f7 25%, #f8fafc 50%, #eef2f7 75%);
    background-size: 200% 100%;
    animation: sa-xray-shimmer 1.2s ease-in-out infinite;
    border: 1px solid #e8eef5;
  }
  @keyframes sa-xray-shimmer {
    0% { background-position: 100% 0; }
    100% { background-position: -100% 0; }
  }
`;

const JOB_POLL_MS = 1500;
const RESULT_RETRY_MS = 1500;
const RESULT_RETRY_MAX = 12;

function isResultPayload(payload) {
  return Boolean(
    payload &&
      (payload.overview ||
        payload.application ||
        payload.architecture ||
        payload.business_modules ||
        payload.index ||
        payload.discovery ||
        payload.analysis)
  );
}

function unwrapResult(payload) {
  if (isResultPayload(payload)) return payload;
  if (isResultPayload(payload?.data)) return payload.data;
  if (isResultPayload(payload?.result)) return payload.result;
  return null;
}

function resultErrorDetail(payload, err) {
  return String(
    payload?.data?.detail ||
      payload?.errorMessage ||
      err?.response?.data?.detail ||
      err?.message ||
      ""
  );
}

function isRetryableResultError(payload, err) {
  const status = err?.response?.status;
  const detail = resultErrorDetail(payload, err).toLowerCase();
  return (
    status === 409 ||
    status === 500 ||
    /still running|not completed|missing|not found/.test(detail)
  );
}

function numericProgress(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : fallback;
}

function readJobBody(job) {
  const nested = job?.data;
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return { ...job, ...nested };
  }
  return job || {};
}

function isScanJobFinished(job, progress) {
  const status = String(job?.status || "").toLowerCase();
  if (["completed", "complete", "success", "succeeded", "analyzed", "done"].includes(status)) {
    return true;
  }
  return progress >= 100 && !["failed", "error", "failure"].includes(status);
}

function splitSourcePath(fullPath) {
  const parts = String(fullPath || "")
    .replace(/\\/g, "/")
    .replace(/^\/+/, "")
    .split("/")
    .filter(Boolean);
  const file = parts.pop() || "";
  return { path: parts.join("/"), file };
}

function resolveWorkspaceId(payload, fallbackId) {
  return (
    payload?.application?.workspace_id ||
    payload?.application?.workspaceId ||
    payload?.workspace_id ||
    payload?.workspaceId ||
    fallbackId ||
    payload?.application?.id ||
    ""
  );
}

function extractFileContents(payload) {
  if (typeof payload === "string") return payload;
  const nested = payload?.data;
  if (typeof nested === "string") return nested;
  const body = nested && typeof nested === "object" ? nested : payload;
  const raw =
    body?.contents ??
    body?.content ??
    body?.fileContent ??
    body?.source ??
    body?.text;
  return typeof raw === "string" ? raw : null;
}

function looksLikeUuid(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(value || ""));
}

function firstReadableName(...values) {
  const readable = values
    .map((value) => String(value || "").trim())
    .filter((value) => value && !looksLikeUuid(value));
  if (readable.length) return readable[0];
  return values.map((value) => String(value || "").trim()).find(Boolean) || "";
}

function isProductId(id) {
  return String(id || "").startsWith("prod_");
}

function workspaceIdFromResultPath(path) {
  const match = String(path || "").match(/workspaces\/([^/]+)\//);
  return match?.[1] || "";
}

function unwrapProducts(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.products)) return payload.products;
  if (Array.isArray(payload?.data?.products)) return payload.data.products;
  return [];
}

function latestAnalysisFrom(detail) {
  const analyses =
    detail?.latest_analyses ||
    detail?.latest_analyses ||
    detail?.analyses ||
    [];
  return Array.isArray(analyses) && analyses.length ? analyses[0] : null;
}

function formatScanDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function sourceLabel(type) {
  const key = String(type || "").toLowerCase();
  if (key === "github") return "GitHub";
  if (key === "upload") return "Upload";
  if (key === "gitlab") return "GitLab";
  if (key === "bitbucket") return "Bitbucket";
  return type ? String(type) : "Source";
}

function scanStatusMeta(status) {
  const key = String(status || "").toLowerCase();
  if (key === "completed" || key === "success" || key === "analyzed") {
    return { label: "Analyzed", className: "is-ready" };
  }
  if (key === "failed" || key === "error") {
    return { label: "Failed", className: "is-failed" };
  }
  if (key === "running" || key === "queued" || key === "in_progress" || key === "processing") {
    return { label: "Scanning", className: "is-busy" };
  }
  return { label: status || "Ready", className: "is-ready" };
}

function mergeProductRecord(product, detail) {
  const body = detail?.product || product || {};
  const repos = detail?.repositories || [];
  const repo = repos[0] || {};
  const analysis = latestAnalysisFrom(detail);
  return {
    product_id: body.product_id || product?.product_id || product?.id,
    name: firstReadableName(body.name, product?.name, repo.name) || "Imported application",
    description: body.description || product?.description || "",
    created_at: body.created_at || product?.created_at,
    updated_at: body.updated_at || product?.updated_at,
    source_type: repo.source_type,
    source_url: repo.source_url,
    repository_name: repo.name,
    status: analysis?.status,
    completed_at: analysis?.completed_at || analysis?.updated_at || body.updated_at,
    analysis_count: detail?.analysis_count,
    workspace_id: workspaceIdFromResultPath(analysis?.result_path),
    project_id: body.project_id || body.projectId || product?.project_id || "",
  };
}

function pickProductProjectId(detail, product) {
  const body = detail?.product || product || {};
  return String(
    body.project_id ||
      body.projectId ||
      body.xray_project_id ||
      body.xrayProjectId ||
      detail?.project_id ||
      detail?.projectId ||
      product?.project_id ||
      ""
  ).trim();
}

function withScanName(data, name, projectId) {
  if (!data) return data;
  return {
    ...data,
    application: {
      ...(data.application || {}),
      ...(name ? { name } : {}),
      ...(projectId ? { project_id: projectId, projectId } : {}),
    },
    overview: {
      ...(data.overview || {}),
      ...(name ? { name } : {}),
    },
  };
}

function rememberXrayProject(productId, projectId) {
  if (!productId || !projectId) return;
  try {
    const map = JSON.parse(sessionStorage.getItem("sa.xrayProjectByProduct") || "{}");
    map[String(productId)] = String(projectId);
    sessionStorage.setItem("sa.xrayProjectByProduct", JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

function recalledXrayProject(productId) {
  if (!productId) return "";
  try {
    const map = JSON.parse(sessionStorage.getItem("sa.xrayProjectByProduct") || "{}");
    return String(map[String(productId)] || "");
  } catch {
    return "";
  }
}

async function resolveXrayProject({ productDetail, product, fallback }) {
  const body = productDetail?.product || product || {};
  const name = firstReadableName(
    fallback,
    body.name,
    body.display_name,
    body.displayName,
    body.title,
    product?.name
  );
  const linkedId = pickProductProjectId(productDetail, product);
  const productId = body.product_id || product?.product_id || productDetail?.product_id;
  const remembered = recalledXrayProject(productId);
  if (linkedId) {
    rememberXrayProject(productId, linkedId);
    return { id: linkedId, name };
  }
  if (remembered) return { id: remembered, name };
  try {
    const listed = await AgentService.getProject(0, { appType: "XRAY" }, default_size);
    const rows = listed?.projectDTOS?.content || [];
    const match = rows.find((row) => {
      if (String(row?.appType || "").toUpperCase() !== "XRAY") return false;
      const rowProduct = row?.productId || row?.product_id;
      if (productId && rowProduct && String(rowProduct) === String(productId)) return true;
      return false;
    });
    const id = String(match?.id || match?._id || "");
    if (id) rememberXrayProject(productId, id);
    return { id, name };
  } catch {
    return { id: "", name };
  }
}

function normalizeProductOverview(overviewPayload, productDetail) {
  const product = overviewPayload?.product || productDetail?.product || {};
  const repos = overviewPayload?.repositories || productDetail?.repositories || [];
  const primary = repos[0] || {};
  const inventory = overviewPayload?.inventory || {};
  const findings = overviewPayload?.top_findings || [];
  const analysis = latestAnalysisFrom(productDetail);
  const workspaceId = workspaceIdFromResultPath(analysis?.result_path);
  return {
    application: {
      id: workspaceId,
      name: firstReadableName(product.name, primary.name, primary.repository_name),
      product_id: product.product_id || overviewPayload?.product_id,
      files: inventory.files,
    },
    overview: {
      name: firstReadableName(product.name, primary.name, primary.repository_name),
      health: {
        overall: overviewPayload?.health?.overall ?? primary.health?.overall,
        risk_level: overviewPayload?.health?.risk_level || primary.health?.risk_level,
        categories: primary.health?.categories || {},
        severity_counts: overviewPayload?.severity_counts || primary.health?.severity_counts || {},
        total_findings: primary.health?.total_findings,
      },
      inventory: {
        files: inventory.files,
        lines: inventory.lines,
        dependencies: inventory.dependencies,
      },
      top_findings: findings,
    },
    analysis: { findings },
  };
}

const importBasePath = (pathname) =>
  (pathname || "").startsWith("/xray") ? "/xray" : "/apps/import";

function shouldOpenImportSources(location) {
  if (location?.state?.openImportSources) return true;
  const value = String(new URLSearchParams(location?.search || "").get("import") || "").toLowerCase();
  return value === "1" || value === "true" || value === "all" || value === "sources";
}

const ImportApplicationPageContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { applicationId } = useParams();
  const importRoot = importBasePath(location.pathname);
  const [importing, setImporting] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [importProgress, setImportProgress] = useState(null);
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [scanName, setScanName] = useState("");
  const [xrayProjectId, setXrayProjectId] = useState("");
  const [fileContent, setFileContent] = useState(null);
  const [fileContentLoading, setFileContentLoading] = useState(false);
  const [fileContentError, setFileContentError] = useState("");
  const [workspaceMode, setWorkspaceMode] = useState("");
  const pollTimeoutRef = useRef(null);
  const cancelledRef = useRef(false);
  const progressRef = useRef(0);
  const fileRequestRef = useRef(0);
  const overviewRequestRef = useRef(0);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState("");
  const [showImportForm, setShowImportForm] = useState(() => shouldOpenImportSources(location));
  const isWorking = Boolean(importing || reanalyzing || importProgress);
  const showOverview = Boolean(applicationId) && !isWorking;
  const navProjectName = location.state?.projectName || "";
  const navProjectId = location.state?.xrayProjectId || "";
  const appName = firstReadableName(
    scanName,
    navProjectName,
    overview?.application?.name,
    overview?.application?.repo
  );
  const productIdForReanalyze =
    (isProductId(applicationId) && applicationId) ||
    overview?.application?.product_id ||
    overview?.product_id ||
    overview?.application?.productId ||
    "";

  React.useEffect(() => {
    const id = "sa-import-page-styles";
    let tag = document.getElementById(id);
    if (!tag) {
      tag = document.createElement("style");
      tag.id = id;
      document.head.appendChild(tag);
    }
    tag.textContent = PAGE_CSS;
  }, []);

  React.useEffect(() => {
    if (!shouldOpenImportSources(location)) return;
    setShowImportForm(true);
  }, [location]);

  const loadResultPayload = useCallback(async (id, maxAttempts = RESULT_RETRY_MAX) => {
    for (let attempt = 0; attempt <= maxAttempts; attempt += 1) {
      if (cancelledRef.current) return null;
      try {
        const payload = await CodeIntelligenceService.getResult(id);
        if (payload?.status === API_RESPONSE.FAILED) {
          if (attempt < maxAttempts && isRetryableResultError(payload)) {
            await new Promise((resolve) => setTimeout(resolve, RESULT_RETRY_MS));
            continue;
          }
          return { error: payload?.errorMessage || "Failed to load overview." };
        }
        const data = unwrapResult(payload);
        if (data) return { data };
        if (attempt < maxAttempts) {
          await new Promise((resolve) => setTimeout(resolve, RESULT_RETRY_MS));
          continue;
        }
        return { error: "Insights are not ready yet. Try refreshing." };
      } catch (err) {
        if (attempt < maxAttempts && isRetryableResultError(null, err)) {
          await new Promise((resolve) => setTimeout(resolve, RESULT_RETRY_MS));
          continue;
        }
        return { error: err?.message || "Failed to load overview." };
      }
    }
    return { error: "Failed to load overview." };
  }, []);

  const loadOverview = useCallback(async (id, { soft = false } = {}) => {
    if (!id) return;
    const requestId = ++overviewRequestRef.current;
    setOverviewLoading(true);
    if (!soft) setOverview(null);
    try {
      let resultId = id;
      let productDetail = null;
      if (navProjectName) setScanName(navProjectName);
      if (navProjectId) setXrayProjectId(navProjectId);
      const remembered = recalledXrayProject(id);
      if (remembered) setXrayProjectId(remembered);
      if (isProductId(id)) {
        const detail = await CodeIntelligenceService.getProduct(id);
        if (overviewRequestRef.current !== requestId) return;
        if (detail?.status === API_RESPONSE.FAILED) {
          showNotificationGlobal(detail?.errorMessage || "Failed to load scan.", "error");
          return;
        }
        productDetail = detail;
        const earlyName = firstReadableName(
          navProjectName,
          detail?.product?.name,
          detail?.repositories?.[0]?.name
        );
        if (earlyName) setScanName(earlyName);
        const workspaceId = workspaceIdFromResultPath(latestAnalysisFrom(detail)?.result_path);
        if (workspaceId) resultId = workspaceId;
      }

      const projectPromise = resolveXrayProject({
        productDetail,
        product: productDetail?.product,
        fallback: navProjectName,
      });
      const loaded = await loadResultPayload(resultId, isProductId(id) ? 1 : RESULT_RETRY_MAX);
      const resolved = await projectPromise;
      if (overviewRequestRef.current !== requestId) return;
      if (resolved?.name) setScanName(resolved.name);
      if (resolved?.id) {
        setXrayProjectId(resolved.id);
        rememberXrayProject(id, resolved.id);
      }

      if (loaded?.data) {
        setOverview(withScanName(loaded.data, resolved?.name, resolved?.id));
        return;
      }

      if (isProductId(id)) {
        const overviewPayload = await CodeIntelligenceService.getProductOverview(id);
        if (overviewRequestRef.current !== requestId) return;
        if (overviewPayload?.status !== API_RESPONSE.FAILED && overviewPayload?.product_id) {
          setOverview(
            withScanName(normalizeProductOverview(overviewPayload, productDetail), resolved?.name, resolved?.id)
          );
          return;
        }
      }

      showNotificationGlobal(loaded?.error || "Failed to load overview.", "error");
    } finally {
      if (overviewRequestRef.current === requestId) setOverviewLoading(false);
    }
  }, [loadResultPayload, navProjectName, navProjectId]);

  const handleRefreshOverview = useCallback(() => {
    if (!applicationId || reanalyzing || overviewLoading) return;
    loadOverview(applicationId, { soft: true });
  }, [applicationId, reanalyzing, overviewLoading, loadOverview]);

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (applicationId) loadOverview(applicationId);
  }, [applicationId, loadOverview]);

  useEffect(() => {
    if (!applicationId) {
      setScanName("");
      setXrayProjectId("");
      return;
    }
    if (navProjectName) {
      setScanName(navProjectName);
    }
    if (navProjectId) {
      setXrayProjectId(navProjectId);
      return;
    }
    const remembered = recalledXrayProject(applicationId);
    if (remembered) setXrayProjectId(remembered);
    const fromList = products.find((item) => item.product_id === applicationId);
    if (fromList?.name && !navProjectName) setScanName(fromList.name);
    if (fromList?.project_id) setXrayProjectId(fromList.project_id);
  }, [applicationId, navProjectName, navProjectId, products]);

  useEffect(() => {
    if (!applicationId) setShowImportForm(false);
  }, [applicationId]);

  useEffect(() => {
    if (applicationId) return undefined;
    let alive = true;
    (async () => {
      setProductsLoading(true);
      setProductsError("");
      try {
        const payload = await CodeIntelligenceService.listProducts();
        if (!alive) return;
        if (payload?.status === API_RESPONSE.FAILED) {
          setProducts([]);
          setProductsError(payload?.errorMessage || "Failed to load scanned applications.");
          return;
        }
        const list = unwrapProducts(payload);
        const enriched = await Promise.all(
          list.map(async (item) => {
            const id = item.product_id || item.id;
            if (!id) return mergeProductRecord(item);
            const detail = await CodeIntelligenceService.getProduct(id);
            if (!alive) return mergeProductRecord(item);
            if (detail?.status === API_RESPONSE.FAILED) return mergeProductRecord(item);
            return mergeProductRecord(item, detail);
          })
        );
        if (!alive) return;
        const sorted = enriched
          .filter((item) => item.product_id)
          .sort(
            (a, b) =>
              new Date(b.completed_at || b.updated_at || 0) -
              new Date(a.completed_at || a.updated_at || 0)
          );
        setProducts(sorted);
      } catch (err) {
        if (!alive) return;
        setProducts([]);
        setProductsError(err?.message || "Failed to load scanned applications.");
      } finally {
        if (alive) setProductsLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [applicationId]);

  const handleFileOpen = useCallback(
    async (fullPath) => {
      const requestId = ++fileRequestRef.current;
      const workspaceId = resolveWorkspaceId(
        overview,
        isProductId(applicationId) ? overview?.application?.application_id : applicationId
      );
      const { path, file } = splitSourcePath(fullPath);
      if (!workspaceId || !file) {
        setFileContent(null);
        setFileContentError("Missing workspace or file path.");
        return;
      }
      setFileContent(null);
      setFileContentError("");
      setFileContentLoading(true);
      try {
        const payload = await CodeIntelligenceService.getLocalSource({ workspaceId, path, file });
        if (requestId !== fileRequestRef.current) return;
        if (payload?.status === API_RESPONSE.FAILED) {
          setFileContentError(payload?.errorMessage || "Failed to load file.");
          return;
        }
        const contents = extractFileContents(payload);
        if (contents == null) {
          setFileContentError("No file content returned.");
          return;
        }
        setFileContentError("");
        setFileContent(contents);
      } catch (err) {
        if (requestId !== fileRequestRef.current) return;
        setFileContentError(err?.message || "Failed to load file.");
      } finally {
        if (requestId === fileRequestRef.current) setFileContentLoading(false);
      }
    },
    [applicationId, overview]
  );

  const waitForJob = (jobId) =>
    new Promise((resolve, reject) => {
      const tick = async () => {
        if (cancelledRef.current) {
          reject(new Error("Import cancelled"));
          return;
        }
        try {
          const job = readJobBody(await CodeIntelligenceService.getJob(jobId));
          if (job?.status === API_RESPONSE.FAILED || job?.status === API_RESPONSE.FAILURE) {
            reject(new Error(job.errorMessage || job.error || "Import failed"));
            return;
          }
          const incoming = numericProgress(
            job?.progress ?? job?.percent ?? job?.percentage,
            progressRef.current
          );
          const nextProgress = Math.max(progressRef.current, incoming);
          progressRef.current = nextProgress;
          setImportProgress((prev) => ({
            ...(prev || {}),
            progress: nextProgress,
            stage: job?.stage,
            stageDetail: job?.stage_detail || job?.stageDetail,
            status: job?.status,
          }));
          const status = String(job?.status || "").toLowerCase();
          if (status === "failed" || status === "failure" || status === "error" || job?.error) {
            reject(new Error(job.error || job.stage_detail || "Import failed"));
            return;
          }
          if (isScanJobFinished(job, nextProgress)) {
            progressRef.current = 100;
            setImportProgress((prev) => ({
              ...(prev || {}),
              progress: 100,
              stage: job?.stage || "completed",
              stageDetail: job?.stage_detail || job?.stageDetail || "Opening insights",
              status: "completed",
            }));
            resolve(job);
            return;
          }
          pollTimeoutRef.current = setTimeout(tick, JOB_POLL_MS);
        } catch (err) {
          reject(err);
        }
      };
      tick();
    });

  const watchImportJob = async (started, label, projectId) => {
    if (started?.status === API_RESPONSE.FAILED) {
      setImportProgress(null);
      showNotificationGlobal(started?.errorMessage || "Import failed.", "error");
      return;
    }
    const jobId = started?.job_id || started?.jobId;
    if (!jobId) {
      setImportProgress(null);
      showNotificationGlobal("Import did not return a job id.", "error");
      return;
    }
    const startedProgress = numericProgress(started.progress, 0);
    progressRef.current = startedProgress;
    setImportProgress((prev) => ({
      ...(prev || {}),
      progress: startedProgress,
      stage: started.stage || "queued",
      stageDetail: started.stage_detail || started.stageDetail || "Waiting for worker",
      status: started.status,
      repo: label,
    }));
    const job = await waitForJob(jobId);
    const importedId =
      job?.product_id ||
      job?.productId ||
      started?.product_id ||
      started?.productId ||
      job?.application_id ||
      job?.applicationId ||
      started?.application_id ||
      started?.applicationId;
    progressRef.current = 0;
    setImportProgress(null);
    if (importedId) {
      rememberXrayProject(importedId, projectId);
      navigate(`${importRoot}/${importedId}`, {
        replace: true,
        state: { projectName: label, xrayProjectId: projectId },
      });
    } else {
      showNotificationGlobal("Import completed.", "success");
    }
  };

  const handleReanalyze = async () => {
    const productId = String(productIdForReanalyze || "").trim();
    if (!productId || reanalyzing || importing) return;
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    setReanalyzing(true);
    progressRef.current = 0;
    setImportProgress({
      progress: 0,
      stage: "queued",
      stageDetail: "Queuing reanalysis",
      status: "queued",
      repo: appName || productId,
      mode: "reanalyze",
    });
    try {
      const started = await CodeIntelligenceService.reanalyzeProduct(productId);
      if (
        started?.status === API_RESPONSE.FAILED ||
        started?.status === API_RESPONSE.FAILURE
      ) {
        throw new Error(started?.errorMessage || "Failed to start reanalysis.");
      }
      const jobId = started?.job_id || started?.jobId || started?.data?.job_id || started?.data?.jobId;
      if (!jobId) {
        throw new Error("Reanalyze did not return a job id.");
      }
      const startedProgress = numericProgress(started.progress ?? started?.data?.progress, 0);
      progressRef.current = startedProgress;
      setImportProgress((prev) => ({
        ...(prev || {}),
        progress: startedProgress,
        stage: started.stage || started?.data?.stage || "queued",
        stageDetail:
          started.stage_detail ||
          started.stageDetail ||
          started?.data?.stage_detail ||
          "Reanalyzing codebase",
        status: started.status || started?.data?.status || "running",
        repo: appName || productId,
        mode: "reanalyze",
      }));
      await waitForJob(jobId);
      setImportProgress(null);
      await loadOverview(productId);
      showNotificationGlobal("Reanalysis complete.", "success");
    } catch (error) {
      setImportProgress(null);
      showNotificationGlobal(error?.message || "Reanalysis failed. Please try again.", "error");
    } finally {
      setReanalyzing(false);
    }
  };

  const createImportProject = async ({ source, label, repoUrl }) => {
    const formData = new FormData();
    formData.append(
      "project",
      new Blob(
        [
          JSON.stringify({
            name: label,
            appType: "XRAY"
          }),
        ],
        { type: "application/json" }
      )
    );
    const created = await AgentService.xrayProjectCreate(formData);
    if (
      created?.status === API_RESPONSE.FAILED ||
      created?.status === API_RESPONSE.FAILURE
    ) {
      throw new Error(created?.errorMessage || "Failed to create project");
    }
    const projectId =
      created?.projectDTO?.id ||
      created?.projectDTO?.projectId ||
      created?.data?.projectDTO?.id ||
      created?.data?.id ||
      created?.id ||
      created?.projectId;
    if (!projectId) {
      throw new Error("Xray project was created but no project id was returned");
    }
    return String(projectId);
  };

  const handleImportContinue = async (importData) => {
    if (!importData) return;
    setImporting(true);
    progressRef.current = 0;
    try {
      const repoUrl = importData.githubUrl || importData.repoUrl;
      const isUrlImport = importData.source !== "UPLOAD" && Boolean(repoUrl);
      const archiveName = importData.file?.name?.replace(/\.[^/.]+$/, "") || "archive";
      const label = isUrlImport ? repoLabelFromUrl(repoUrl) || repoUrl : archiveName;

      setImportProgress({
        progress: 0,
        stage: "queued",
        stageDetail: "Creating project",
        status: "queued",
        repo: label,
      });

      const projectId = await createImportProject({
        source: isUrlImport ? "GITHUB" : "UPLOAD",
        label,
        repoUrl: isUrlImport ? repoUrl : "",
      });

      if (isUrlImport) {
        setImportProgress((prev) => ({
          ...(prev || {}),
          stageDetail: "Starting import",
          repo: repoUrl,
        }));
        const started = await CodeIntelligenceService.importGithub(repoUrl, { projectId });
        await watchImportJob(started, repoUrl, projectId);
        return;
      }

      if (!importData.file) {
        showNotificationGlobal("Please upload a code archive.", "error");
        return;
      }

      setImportProgress((prev) => ({
        ...(prev || {}),
        stageDetail: "Uploading archive",
        repo: archiveName,
      }));
      const started = await CodeIntelligenceService.importUpload(importData.file, { projectId });
      await watchImportJob(started, archiveName, projectId);
    } catch (error) {
      setImportProgress(null);
      showNotificationGlobal(error?.message || "Import failed. Please try again.", "error");
    } finally {
      setImporting(false);
    }
  };

  const heading = showOverview
    ? {
        kicker: "Xray",
        title: appName || (overviewLoading ? "Opening scan" : "Application insights"),
        sub: "Health, architecture, modules, and findings for the imported codebase.",
      }
    : isWorking
      ? {
          kicker: "Xray",
          title: reanalyzing ? "Reanalyzing this application" : "We’re learning this application",
          sub: reanalyzing
            ? "Refreshing architecture, health, and findings for the existing codebase. Insights reload when ready."
            : "Clone, index, and review run in sequence. Insights open automatically when ready.",
        }
      : showImportForm
        ? {
            kicker: "Xray",
            title: "Import an existing project",
            sub: "Connect GitHub or upload an archive. Analysis starts as soon as we have the code.",
          }
        : {
            kicker: "Xray",
            title: "Scanned applications",
            sub: "Review past scans, or import a GitHub repo or archive to start a new one.",
          };

  const pageMode = `${showOverview ? "is-overview" : isWorking ? "is-working" : ""}${workspaceMode === "files" ? " is-files" : ""}`.trim();

  return (
    <div className={`sa-import-page ${pageMode}`.trim()}>
      {!isWorking ? (
        <header className="sa-import-page-header">
          <div className="sa-import-page-header-row">
            <div className="sa-import-page-heading">
              <div className="sa-import-page-kicker">
                <i className="fa-solid fa-microscope" aria-hidden="true" />
                {heading.kicker}
              </div>
              <h1>{heading.title}</h1>
              <p>{heading.sub}</p>
            </div>

            {showOverview ? <div id="ci-kpi-slot" className="sa-import-kpi-slot" /> : null}

            <div className="sa-import-page-actions">
              {!showOverview && !showImportForm && products.length ? (
                <button
                  type="button"
                  className="sa-xray-import-btn"
                  onClick={() => setShowImportForm(true)}
                >
                  <i className="fa-solid fa-plus" aria-hidden="true" />
                  New scan
                </button>
              ) : null}
              <button
                type="button"
                className="sa-import-page-back"
                onClick={() => {
                  if (showOverview) navigate(importRoot);
                  else if (showImportForm) setShowImportForm(false);
                  else navigate("/apps/");
                }}
              >
                <i className="fa-solid fa-arrow-left" aria-hidden="true" />
                {showOverview || showImportForm ? "Back to Xray" : "Back to Home"}
              </button>
            </div>
          </div>
        </header>
      ) : null}

      <div className="sa-import-page-body">
        <div
          className={`sa-import-page-body-inner${showOverview ? " is-overview" : ""}${isWorking ? " is-working" : ""}`}
        >
          {showOverview ? (
            <CodeIntelligenceOverview
              data={overview}
              loading={overviewLoading}
              projectName={appName}
              xrayProjectId={xrayProjectId}
              productId={productIdForReanalyze}
              canReanalyze={Boolean(productIdForReanalyze)}
              onReanalyze={productIdForReanalyze ? handleReanalyze : undefined}
              reanalyzing={reanalyzing}
              onRefresh={handleRefreshOverview}
              refreshing={overviewLoading && Boolean(overview)}
              onFileOpen={handleFileOpen}
              onTabChange={setWorkspaceMode}
              fileContent={fileContent}
              fileContentLoading={fileContentLoading}
              fileContentError={fileContentError}
            />
          ) : isWorking ? (
            <div className="sa-import-page-card">
              <ImportWorkingView
                progress={importProgress}
                repoLabel={repoLabelFromUrl(importProgress?.repo) || importProgress?.repo}
                kicker={reanalyzing || importProgress?.mode === "reanalyze" ? "Reanalyzing..." : "Analyzing your application"}
              />
            </div>
          ) : (
            <div className={`sa-import-page-card${showImportForm ? " is-form" : ""}`}>
              {showImportForm ? (
                <ImportApplicationSource
                  onContinue={handleImportContinue}
                  submitting={importing}
                  hideTitle
                />
              ) : (
                <div className="sa-xray-home">
                  {productsLoading ? (
                    <div className="sa-xray-grid">
                      <div className="sa-xray-skeleton" />
                      <div className="sa-xray-skeleton" />
                      <div className="sa-xray-skeleton" />
                      <div className="sa-xray-skeleton" />
                      <div className="sa-xray-skeleton" />
                      <div className="sa-xray-skeleton" />
                    </div>
                  ) : productsError ? (
                    <div className="sa-xray-error">
                      <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
                      <h3>Couldn’t load scans</h3>
                      <p>{productsError}</p>
                    </div>
                  ) : products.length ? (
                    <>
                      <div className="sa-xray-toolbar">
                        <div className="sa-xray-count">
                          <strong>{products.length}</strong>
                          {products.length === 1 ? " application" : " applications"}
                        </div>
                      </div>
                      <ul className="sa-xray-grid">
                        {products.map((item) => {
                          const status = scanStatusMeta(item.status);
                          const scannedAt = formatScanDate(item.completed_at || item.updated_at);
                          const source = sourceLabel(item.source_type);
                          const isGithub = String(item.source_type || "").toLowerCase() === "github";
                          return (
                            <li key={item.product_id}>
                              <button
                                type="button"
                                className="sa-xray-card"
                                onClick={() =>
                                  navigate(`${importRoot}/${item.product_id}`, {
                                    state: { projectName: item.name },
                                  })
                                }
                              >
                                <div className="sa-xray-card-top">
                                  <span className="sa-xray-card-icon">
                                    <i
                                      className={isGithub ? "fa-brands fa-github" : "fa-solid fa-folder-tree"}
                                      aria-hidden="true"
                                    />
                                  </span>
                                  <span className={`sa-xray-scan-status ${status.className}`}>
                                    {status.label}
                                  </span>
                                </div>
                                <div>
                                  <div className="sa-xray-card-title">{item.name}</div>
                                  <div className="sa-xray-card-source">{source}</div>
                                </div>
                                <div className="sa-xray-card-meta">
                                  <span>{scannedAt ? `Scanned ${scannedAt}` : "Ready to open"}</span>
                                  <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                                </div>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </>
                  ) : (
                    <div className="sa-xray-empty">
                      <div className="sa-xray-empty-glow" aria-hidden="true" />
                      <div className="sa-xray-empty-icon">
                        <i className="fa-solid fa-microscope" aria-hidden="true" />
                      </div>
                      <h3>Start your first Xray scan</h3>
                      <p>
                        Import an existing project to map architecture, health, and findings.
                      </p>
                      <button
                        type="button"
                        className="sa-xray-import-btn"
                        onClick={() => setShowImportForm(true)}
                      >
                        <i className="fa-solid fa-file-import" aria-hidden="true" />
                        Import Existing
                      </button>
                      <div className="sa-xray-trust">
                        <span><i className="fa-solid fa-lock" aria-hidden="true" /> Private</span>
                        <span><i className="fa-solid fa-eye-slash" aria-hidden="true" /> Read-only clone</span>
                        <span><i className="fa-solid fa-shield-halved" aria-hidden="true" /> Never used for training</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function ImportApplicationPage() {
  if (isPocTenant()) {
    return <PremiumFeatureLocked featureName="Xray" />;
  }
  return <ImportApplicationPageContent />;
}
